import React, { useEffect, useState } from "react";
import { useRoute } from "@react-navigation/native";
import i18n from "utils/i18n";
import _ from "lodash";
import ThemedSafeAreaComponentTop from "components/atoms/ThemedSafeAreaTop";
import ThemedLayout from "components/atoms/ThemedLayout";
import LoadingComponent from "components/atoms/Loading";
import { FormStatus } from 'utils/constants/form-constants';
import GraphqlBuilder from "utils/request/graphql/builder";
import MutationManagementEmployee from "graphql/mutations/management/MutationManagementEmployee";
import { constantsGraphql } from "utils/constants/constants";
import FetchManagementEmployee from 'utils/fetch/management/FetchManagementEmployee'
import HelperManagementRights from "utils/helper-management-rights";
import { managementRightsConstants } from "utils/constants/management-right-constants";
import TabbedContent from 'components/molecules/TabbedContent'
import EmployeeData from 'scenes/management/employees/components/employee/data'
import EmployeeAdvanced from 'scenes/management/employees/components/employee/advanced'
import EmployeeStatistics from 'scenes/management/employees/components/employee/statistics'
import EmployeeCalendar from 'scenes/management/employees/components/employee/calendar'
import EmployeeCredits from "scenes/management/employees/components/employee/credits";

const ManagementEmployeesEmployeeScene = () => {
	
	const route = useRoute();
	
	const [ loading, setLoading ] = useState( true )
	const [ employee, setEmployee ] = useState( {} )
	const [ team, setTeam ] = useState( {} )
	
	const employeeGuid = _.get( route.params, 'employeeGuid', null );
	const teamGuid = _.get( route.params, 'teamGuid', null );
	
	const employeeExists = employeeGuid !== null;
	
	const fetchEmployee = ({ loading = true } = {} ) => {
		if( employeeGuid !== null ) {
			FetchManagementEmployee.fetch( employeeGuid, {
				variables: {
					visible_for_employees: constantsGraphql.YES_NO,
					verified: constantsGraphql.YES_NO,
					active: constantsGraphql.YES_NO,
				},
				callbackStart: setLoading.bind( this, loading ),
				callbackSuccess: ( { employee } ) => {
					const team = _.get( employee, 'team', {} );
					
					employee[ 'team_guid' ] = _.get( team, 'guid', null );
					employee[ 'department_guid' ] = _.get( employee, [ 'department', 'guid' ], null );
					employee[ 'jobtitle_guid' ] = _.get( employee, [ 'jobtitle', 'guid' ], null );
					
					for( let key in employee ) {
						if( employee[ key ] === null ) {
							employee[ key ] = ''
						}
					}
					
					HelperManagementRights.setEmployee( employee );
					
					setLoading( false )
					setEmployee( employee )
					setTeam( team )
				},
				callbackError: setLoading.bind( this, false ),
			});
			
			return
		}
		
		// Fill the team guid
		setEmployee({
			team_guid: teamGuid
		})
		
		setLoading( false )
	}
	
	const saveForm = ( values, { callback } = {} ) => {
		// Add verified :)
		values['verified'] = constantsGraphql.YES;
		
		new GraphqlBuilder( { processVariables: false })
			.addQuery( 'mutation', MutationManagementEmployee, values )
			.setResultPath( ['mutation', 'management_employee'], {} )
			.setOnStart( () => {
				callback( FormStatus.LOADING )
			})
			.setOnSuccess( ( result ) => {
				callback( FormStatus.SUCCESS )
				
				setEmployee( result )
			})
			.setOnError( ( result ) => {
				callback( FormStatus.ERROR )
			})
			.execute()
	}
	
	useEffect( () => {
		return fetchEmployee();
	}, [] )
	
	if( loading ) {
		return (
			<LoadingComponent />
		)
	}
	
	const tabBarItems = []
	
	// Statistics
	if( employeeExists ) {
		tabBarItems.push({
			key: 'statistics',
			text: i18n.t( 'management:form_block.title_statistic_other' ),
			scene: ( props ) => {
				return (
					<EmployeeStatistics
						{ ...props }
						employee={ employee }
						team={ team }
					/>
				);
			}
		});
	}
	
	// Data
	tabBarItems.push({
		key: 'data',
		text: i18n.t( 'management:form_block.title_default' ),
		scene: ( props ) => {
			return (
				<EmployeeData
					{ ...props }
					employee={ employee }
					reload={ fetchEmployee.bind( this ) }
					saveForm={ saveForm.bind( this ) }
				/>
			);
		}
	});
	
	// Calendar
	if( employeeExists ) {
		tabBarItems.push({
			key: 'calendar',
			text: i18n.t( 'page.booking.calendar.calendar.title'),
			scene: ( props ) => {
				return (
					<EmployeeCalendar
						{ ...props }
						employee={ employee }
					/>
				);
			}
		});
	}

	// Credits
	if( employeeExists && HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.EMPLOYEES ) ) {
		tabBarItems.push({
			key: 'credits',
			text: i18n.t( 'titles.credits' ),
			scene: ( props ) => {
				return (
					<EmployeeCredits
						{ ...props }
						employee={ employee }
						reload={ fetchEmployee.bind( this ) }
					/>
				);
			}
		});
	}
	
	// Advanced
	if( employeeExists && HelperManagementRights.hasEditRight( managementRightsConstants.EDIT.EMPLOYEES ) ) {
		tabBarItems.push({
			key: 'advanced',
			text: i18n.t('management:pages.employee.advanced.title'),
			scene: ( props ) => {
				return (
					<EmployeeAdvanced
						{ ...props }
						employee={ employee }
					/>
				);
			}
		});
	}
	
	return (
		<ThemedSafeAreaComponentTop
			top={ false }>
			
			<ThemedLayout
				title={ i18n.t('management:pages.teams.employee.title') }
				topNavigation={true}
				showBackButton={true}>
				
				<TabbedContent
					items={ tabBarItems }
				/>
			</ThemedLayout>
		</ThemedSafeAreaComponentTop>
	)
}

export default ManagementEmployeesEmployeeScene
